/**
 * 根据目录结构生成router结构
 * ../views 目录下的所有有name的vue文件
 *
 * ../views/p1/p2/p3.vue, ../views/p1/p2/p3/index.vue  ->  /p1/p2/p3
 *
 *
 * 配置规则：
 * 1、 子目录下的为同级同名vue文件的子路由
 *   /p.vue
 *   /p/c.vue
 *   {
 *     path: '/p',
 *     ...
 *     children: [
 *       {
 *         path: '/p/c',
 *         ...
 *       }
 *     ]
 *   }
 *
 * 2、 名字以 '_' 开头的文件或文件夹为动态路由
 *   /abc/_id/_type.vue  -> /abc/:id/:type
 *   /abc/_type.vue  -> /abc/:type
 *   /abc/_type/index.vue  -> /abc/:type
 *
 * 3、 'View.name.vue' 为路由下的命名视图，视图名称为 name
 *   {
 *      ...
 *      components: {
 *        default: ...,
 *        name: ...,
 *      }
 *      ...
 *   }
 *
 * 4、 使用
 *   Vue.use(name, {
 *     views: 'views', // 默认值 ‘views’ 需要配置的路由页面所在目录 ，为空则不配置
 *     components: 'components', // 默认值 ‘components’ 需要配置的全局组件所在目录 ，为空则不配置
 *     errPath: '', // 错误路由重定向 默认不处理
 *     name: '$autoRouter', // 默认值 ‘$autoRouter’ 挂载到Vue上的名称
 *     config: {
 *       // 每个路由对象的默认配置对象
 *       // 在组件中可单独配置 ex:  home.vue >>  export const routerConfig = {}
 *       // 优先级 不同属性合并， 相同属性 routerConfig > config ;
 *       // 都支持函数方式 config = route => return newRoute;
 *     },
 *   })
 *
 *   $autoRouter: {
 *     allFile: [], // src 下所有 vue文件对象
       components: [], // 所有全局组件
       views: [], // 所有路由组件
       routes: [], // 路由配置
 *   }
 *
 *   @example:
 *   ...
 *   Vue.use(autoRouter, {...})
 *   ...
 *   const router = new VueRouter({
 *     ...
 *     routes: Vue.$autoRouter.routes,
 *     ...
 *   })
 *   ...
 *
 * */

const Pages = require.context('@', true, /^((?!\/node_modules\/).)*\.vue$/);

const AllFile = Pages.keys().map(path => {
  // 保存所有vue文件
  return {__file: path, ...Pages(path)};
});

const setView = (name, parent, view) => {
  // 配置路由及其下的所有子路由的命名视图
  if (parent) {
    parent.components[name] = view;
    parent.children && parent.children.map(child => setView(name, child, view));
  }
};

const delNoPath = routes => {
  // 去掉无路径的多余项
  for (let [ind, route] of routes.entries()) {
    if (route.path) {
      delNoPath(route.children);
    } else {
      routes.splice(ind, 1, ...route.children);
      delNoPath(routes);
    }
  }
};

const initRoute = (route, config) => {
  // 合并路由配置
  if (typeof config === 'function') {
    return config(route);
  } else if (typeof config === 'object') {
    config = JSON.parse(JSON.stringify(config || {}));
    config.meta = Object.assign(route.meta, config.meta);
    return Object.assign(route, config);
  }
  return route;
};

const findRouteByName = function (name, routes) {
  routes = routes || this && this.routes || [];
  if (!routes.length) return null;
  for (let route of routes) {
    if (route.name === name) {
      return route;
    } else if (route.children) {
      let item = findRouteByName(name, route.children);
      if (item) return item;
    }
  }
  return null;
};

export default {
  install(Vue, options = {}) {
    let opt = Object.assign({
      views: 'views',
      components: 'components',
      name: '$autoRouter',
      errPath: '',
      config: {},
    }, options);
    let {views, components, config, errPath, name: $autoRouter} = opt;
    let autoRouter = {
      allFile: AllFile,
      components: [],
      views: [],
      routes: [],
      findRouteByName,
    };
    let viewsReg = new RegExp(`^src/${views}/`);
    let compReg = new RegExp(`^src/${components}/`);
    if (process.env.NODE_ENV === 'production') {
      viewsReg = new RegExp(`^\\./${views}/`);
      compReg = new RegExp(`^\\./${components}/`);
    }
    let viewReg = /View\.\w+$/; // 判断是否为命名视图;
    let viewObject = {}; // 所有命名视图{组件名：{视图名：视图组件}}

    AllFile.forEach(files => {
      let {__file, routerConfig} = files;
      let file = files.default || files;
      __file = file.__file || __file;
      let path = __file.slice(0, -4).replace(/\/index$/i, '').split('/');
      if (views && viewsReg.test(__file)) {
        path = path.slice(views.split('/').length + 1);
        // 需要自动配置路由的页面
        autoRouter.views.push(file);

        let len = path.length;
        let curr = autoRouter.routes;
        let parent = null;
        let name = '';

        path.forEach((key, ind) => {
          name += key.replace(/^[a-z]/i, s => s.toLocaleUpperCase()).replace(/^_.*/i, '');
          let child = curr.find(item => item.name === name);
          if (ind + 1 === len) {
            // 文件
            if (viewReg.test(key)) {
              // 命名视图
              if (parent) {
                viewObject[parent.name] = viewObject[parent.name] || {};
                viewObject[parent.name][key.split('.')[1]] = file;
              }
            } else {
              // 子路由
              let route = {
                name,
                path: '/' + path.map(s => s.toLocaleLowerCase().replace(/^_/, ':').replace(/_$/, '?')).join('/'),
                meta: {},
                children: [],
                components: {
                  default: file,
                },
              };
              route = initRoute(route, config || {});
              route = initRoute(route, routerConfig);
              if (!parent) {
                let c = {...route};
                delete c.children;
                parent = Object.assign(child || {}, c);
              }
              !child && curr.push(route);
              curr = autoRouter.routes;
            }
          } else if (child) {
            parent = child;
            curr = child.children || [];
          } else {
            let route = {
              name,
              components: {},
              children: [],
            };
            curr.push(route);
            parent = route;
            curr = route.children;
          }
        })
      } else if (components && compReg.test(__file)) {
        // 自动引入的全局组件
        path = path.slice(components.split('/').length + 1);
        autoRouter.components.push(file);
        file.name && Vue.component(path.join('-'), file);
      }
    });
    delNoPath(autoRouter.routes);

    // 设置命名视图
    Object.keys(viewObject).forEach(pName => {
      let parent = findRouteByName(pName, autoRouter.routes);
      Object.keys(viewObject[pName]).forEach(vKey => {
        setView(vKey, parent, viewObject[pName][vKey]);
      })
    });

    viewObject = {};

    errPath && autoRouter.routes.push({path: '*', redirect: errPath});

    Vue.prototype[$autoRouter] = autoRouter;
  }
}